import type { WardrobeViewModel } from '../../hooks/useOutfitWardrobeViewModel';
import type { OutfitProduct } from '../../types/ai';

type OutfitWardrobePanelProps = {
  viewModel: WardrobeViewModel;
};

function WardrobeItemCard({
  product,
  categoryLabel,
  isBuilding = false,
}: {
  product?: OutfitProduct;
  categoryLabel: string;
  isBuilding?: boolean;
}) {
  if (!product) {
    if (isBuilding) {
      return (
        <div
          aria-hidden="true"
          className="flex items-center gap-3 rounded-lg border border-black/10 bg-white px-3 py-3"
        >
          <div className="h-12 w-12 shrink-0 animate-pulse rounded-md bg-black/10" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
              {categoryLabel}
            </p>
            <div className="h-3 w-1/3 animate-pulse rounded bg-black/10" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-black/10" />
          </div>
          <div className="h-4 w-12 shrink-0 animate-pulse rounded bg-black/10" />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed border-black/10 bg-brand-beige-tint-1/50 px-3 py-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-black/5 text-xs text-text-grey">
          —
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
            {categoryLabel}
          </p>
          <p className="mt-0.5 text-xs text-text-grey/60">Inte vald ännu</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-black/10 bg-white px-3 py-3 transition-all duration-200">
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt={product.name}
          className="h-12 w-12 shrink-0 rounded-md object-cover"
        />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-brand-beige-tint-1 text-xs text-text-grey">
          {categoryLabel[0]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-text-grey">
          {categoryLabel}
        </p>
        {product.brand && (
          <p className="mt-0.5 truncate text-xs font-medium text-brand-blue">{product.brand}</p>
        )}
        <p className="truncate text-xs text-brand-blue/80">{product.name}</p>
      </div>
      {product.price != null && (
        <p className="shrink-0 text-xs font-semibold text-brand-blue">
          {product.price.toLocaleString('sv-SE')} kr
        </p>
      )}
    </div>
  );
}

export function OutfitWardrobePanel({ viewModel }: OutfitWardrobePanelProps) {
  if (!viewModel.isActive) return null;

  return (
    <div className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
      <h3 className="text-base font-semibold text-brand-blue">Garderob</h3>
      <p className="mt-1 text-xs text-text-grey">
        Dina valda produkter och outfits byggs upp i realtid.
      </p>

      <div className="mt-4 flex flex-col gap-2">
        {viewModel.categoryOrder.map((role) => (
          <WardrobeItemCard
            key={role}
            product={viewModel.products.get(role)}
            categoryLabel={viewModel.categoryLabels[role] ?? role}
            isBuilding={viewModel.isBuilding}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-black/10 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-grey">Totalt</p>
        {viewModel.isBuilding && viewModel.totalPrice === 0 ? (
          <div className="h-4 w-16 animate-pulse rounded bg-black/10" aria-hidden="true" />
        ) : (
          <p className="text-sm font-semibold text-brand-blue">
            {viewModel.totalPrice.toLocaleString('sv-SE')} kr
          </p>
        )}
      </div>
    </div>
  );
}
